/* ==========================================================================
   Scentera — core behaviour
   Every feature here is an enhancement. The page must be usable without it.
   ========================================================================== */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     Announcements for screen readers
     ---------------------------------------------------------------------- */

  const liveRegion = () => document.getElementById('LiveRegion');

  window.announce = function (message) {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  };

  /* ------------------------------------------------------------------------
     Focus trapping — shared by drawers and modals
     ---------------------------------------------------------------------- */

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  let trapHandler = null;

  function trapFocus(container, initialFocus) {
    removeTrapFocus();

    const focusable = () => Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);

    trapHandler = function (event) {
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', trapHandler);
    (initialFocus || focusable()[0] || container).focus({ preventScroll: true });
  }

  function removeTrapFocus(returnFocusTo) {
    if (trapHandler) {
      document.removeEventListener('keydown', trapHandler);
      trapHandler = null;
    }
    if (returnFocusTo) returnFocusTo.focus({ preventScroll: true });
  }

  window.trapFocus = trapFocus;
  window.removeTrapFocus = removeTrapFocus;

  /* ------------------------------------------------------------------------
     Scroll lock — preserves position, avoids the iOS jump
     ---------------------------------------------------------------------- */

  let scrollPosition = 0;
  let lockCount = 0;

  function lockScroll() {
    if (lockCount === 0) {
      scrollPosition = window.scrollY;
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.width = '100%';
      if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
    }
    lockCount += 1;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount > 0) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.paddingRight = '';
    window.scrollTo(0, scrollPosition);
  }

  window.lockScroll = lockScroll;
  window.unlockScroll = unlockScroll;

  /* ------------------------------------------------------------------------
     Reveal on enter — one observer for the whole page
     ---------------------------------------------------------------------- */

  function initReveal() {
    const targets = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (targets.length === 0) return;

    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );

    targets.forEach((el) => observer.observe(el));
  }

  /* ------------------------------------------------------------------------
     <scentera-drawer> — menu, cart, search
     Markup owns the open/close buttons; this only manages state.
     ---------------------------------------------------------------------- */

  class ScenteraDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.opener = null;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]') || event.target === this.overlay) {
          event.preventDefault();
          this.close();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.hasAttribute('open')) {
          event.preventDefault();
          this.close();
        }
      });
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.setAttribute('open', '');
      lockScroll();
      // Wait a frame so the transition runs from the closed state.
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        trapFocus(this.panel, this.querySelector('[data-drawer-close]'));
      });
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.classList.remove('is-open');
      removeTrapFocus(this.opener);
      unlockScroll();

      const finish = () => {
        this.removeAttribute('open');
        this.panel.removeEventListener('transitionend', finish);
      };

      if (prefersReducedMotion.matches) {
        finish();
      } else {
        this.panel.addEventListener('transitionend', finish);
        // Safety net if the transition never fires.
        window.setTimeout(finish, 600);
      }

      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    toggle(opener) {
      this.hasAttribute('open') ? this.close() : this.open(opener);
    }
  }

  customElements.define('scentera-drawer', ScenteraDrawer);

  /* Buttons that open a drawer by id */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
    if (!drawer) return;
    event.preventDefault();
    drawer.open(trigger);
  });

  /* ------------------------------------------------------------------------
     <scentera-accordion> — animates <details> to its real height
     ---------------------------------------------------------------------- */

  class ScenteraAccordion extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('[data-accordion-content]');
      if (!this.details || !this.summary || !this.content) return;

      this.animation = null;
      this.summary.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      if (prefersReducedMotion.matches) return;
      event.preventDefault();
      this.details.style.overflow = 'hidden';
      this.details.open ? this.shrink() : this.expand();
    }

    expand() {
      const start = this.details.offsetHeight;
      this.details.open = true;
      const end = this.summary.offsetHeight + this.content.offsetHeight;
      this.animate(start, end, true);
    }

    shrink() {
      const start = this.details.offsetHeight;
      const end = this.summary.offsetHeight;
      this.animate(start, end, false);
    }

    animate(start, end, willOpen) {
      if (this.animation) this.animation.cancel();

      this.animation = this.details.animate(
        { height: [`${start}px`, `${end}px`] },
        { duration: 300, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' }
      );

      this.animation.onfinish = () => {
        this.details.open = willOpen;
        this.details.style.height = '';
        this.details.style.overflow = '';
        this.animation = null;
      };
    }
  }

  customElements.define('scentera-accordion', ScenteraAccordion);

  /* ------------------------------------------------------------------------
     <quantity-input>
     ---------------------------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = button.name === 'plus' ? 1 : -1;
          const min = parseInt(this.input.min || '0', 10);
          const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
          const next = Math.min(max, Math.max(min, parseInt(this.input.value, 10) + step));

          if (next === parseInt(this.input.value, 10)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------------
     Cart interface — one place that knows how to talk to /cart
     ---------------------------------------------------------------------- */

  const Cart = {
    routes: window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/',

    sectionsToRender() {
      return Array.from(document.querySelectorAll('[data-cart-section]'))
        .map((el) => el.getAttribute('data-cart-section'))
        .filter((v, i, a) => a.indexOf(v) === i);
    },

    async request(path, body) {
      const response = await fetch(`${this.routes}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok) {
        const error = new Error(data.description || data.message || 'Cart error');
        error.data = data;
        throw error;
      }
      return data;
    },

    async add(formData) {
      const body = {
        items: formData.items,
        sections: this.sectionsToRender(),
        sections_url: window.location.pathname,
      };
      const data = await this.request('cart/add.js', body);
      this.renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },

    async change(line, quantity) {
      const data = await this.request('cart/change.js', {
        line,
        quantity,
        sections: this.sectionsToRender(),
        sections_url: window.location.pathname,
      });
      this.renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },

    renderSections(sections) {
      if (!sections) return;
      document.querySelectorAll('[data-cart-section]').forEach((el) => {
        const id = el.getAttribute('data-cart-section');
        const html = sections[id];
        if (!html) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector(`[data-cart-section="${id}"]`);
        if (source) el.innerHTML = source.innerHTML;
      });
    },
  };

  window.ScenteraCart = Cart;

  /* ------------------------------------------------------------------------
     Product form — add to cart with real feedback
     ---------------------------------------------------------------------- */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.button = this.querySelector('[data-add-to-cart]');
      this.errorTarget = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      // Without JS the form posts to /cart/add and Shopify handles it. With JS
      // we intercept so the drawer can open in place.
      event.preventDefault();
      if (this.button && this.button.getAttribute('aria-disabled') === 'true') return;

      this.setLoading(true);
      this.clearError();

      const formData = new FormData(this.form);
      const items = [
        {
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity') || '1', 10),
        },
      ];

      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value) properties[match[1]] = value;
      });
      if (Object.keys(properties).length) items[0].properties = properties;

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) items[0].selling_plan = sellingPlan;

      try {
        const cart = await Cart.add({ items });
        const note = document.getElementById('CartNotification');
        const added = cart.items ? cart.items[0] : cart;
        if (note && typeof note.show === 'function') {
          note.show({
            title: added.product_title || added.title || '',
            variant: added.variant_title || '',
          });
        }
      } catch (error) {
        this.showError(error.data && error.data.description ? error.data.description : error.message);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(state) {
      if (!this.button) return;
      this.button.setAttribute('data-loading', state ? 'true' : 'false');
      this.button.setAttribute('aria-busy', state ? 'true' : 'false');
    }

    showError(message) {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = message;
      this.errorTarget.hidden = false;
      window.announce(message);
    }

    clearError() {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = '';
      this.errorTarget.hidden = true;
    }
  }

  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------------
     <cart-notification>
     Announces the add without stealing focus. Anyone mid-task keeps their place;
     anyone who wants the cart has one link to it.
     ---------------------------------------------------------------------- */

  class CartNotification extends HTMLElement {
    connectedCallback() {
      this.titleEl = this.querySelector('[data-note-title]');
      this.variantEl = this.querySelector('[data-note-variant]');
      this.timer = null;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-note-close]')) this.hide();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.hidden) this.hide();
      });
    }

    show(detail) {
      window.clearTimeout(this.timer);

      if (this.titleEl) this.titleEl.textContent = detail.title;
      if (this.variantEl) this.variantEl.textContent = detail.variant || '';

      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-visible'));

      // Long enough to read and act on, short enough not to sit in the way.
      this.timer = window.setTimeout(() => this.hide(), 6000);
    }

    hide() {
      window.clearTimeout(this.timer);
      this.classList.remove('is-visible');

      const finish = () => {
        this.hidden = true;
        this.removeEventListener('transitionend', finish);
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
      } else {
        this.addEventListener('transitionend', finish);
        window.setTimeout(finish, 500);
      }
    }
  }

  customElements.define('cart-notification', CartNotification);

  /* ------------------------------------------------------------------------
     Cart line items — quantity change and remove
     ---------------------------------------------------------------------- */

  document.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;

    const line = parseInt(input.getAttribute('data-line'), 10);
    const quantity = parseInt(input.value, 10);
    const container = input.closest('[data-cart-item]');
    if (container) container.setAttribute('data-updating', 'true');

    try {
      const cart = await Cart.change(line, quantity);
      const item = cart.items[line - 1];
      if (item && item.quantity < quantity) {
        window.announce(input.getAttribute('data-limit-message') || '');
      }
    } catch (error) {
      window.announce(error.message);
    }
  });

  document.addEventListener('click', async (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();

    const line = parseInt(remove.getAttribute('data-line'), 10);
    const container = remove.closest('[data-cart-item]');
    if (container) container.setAttribute('data-updating', 'true');

    try {
      await Cart.change(line, 0);
      window.announce(remove.getAttribute('data-removed-message') || '');
    } catch (error) {
      window.announce(error.message);
    }
  });

  /* ------------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function init() {
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sections added or moved in the theme editor need re-initialising.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', initReveal);
})();
